import { LoaderCircle, Menu, PanelLeftClose, PanelLeftOpen, Search, Shield, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { RuntimePill } from "@/components/common/RuntimePill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BUILD_ENV } from "@/lib/buildInfo";
import { formatRelative } from "@/lib/format";
import { PermissionBlockedDialog } from "@/features/system/PermissionBlockedDialog";
import { usePreferences } from "@/lib/storage";
import { useIsDesktop, useIsLarge } from "@/lib/useMediaQuery";
import { AdminBell } from "./AdminBell";
import { HelpMenu } from "./HelpMenu";
import { useShellData } from "./shellData";
import { ThemeMenu } from "./ThemeMenu";
import { UserMenu } from "./UserMenu";

export function Header({ onOpenDrawer, onOpenPalette }: { onOpenDrawer: () => void; onOpenPalette: () => void }) {
  const shell = useShellData();
  const [prefs, setPrefs] = usePreferences();
  const isLarge = useIsLarge();
  const isDesktop = useIsDesktop();
  const { pathname } = useLocation();
  const inAdmin = pathname.startsWith("/admin");
  const r = shell.runtime;
  const [allowHelp, setAllowHelp] = useState(false);
  const blocked = r.state === "permission_blocked";

  return (
    <header role="banner" className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur no-print">
      {isLarge ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label={prefs.sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setPrefs({ sidebarCollapsed: !prefs.sidebarCollapsed })}
        >
          {prefs.sidebarCollapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
        </Button>
      ) : (
        <Button variant="ghost" size="icon" aria-label="Open menu" onClick={onOpenDrawer}>
          <Menu aria-hidden />
        </Button>
      )}
      <Link to="/" aria-label="ATATUS Test Suite home" className="flex items-center gap-2 font-semibold">
        <img src="/ats.svg" alt="" className="size-6" />
        <span className="hidden sm:inline">ATATUS Test Suite</span>
      </Link>
      {BUILD_ENV !== "production" && (
        <Badge variant="outline" className="font-mono">
          {BUILD_ENV === "staging" ? "STAGING" : "DEV"}
        </Badge>
      )}
      {isDesktop ? (
        <Button variant="outline" className="ml-2 w-64 justify-start text-muted-foreground" onClick={onOpenPalette}>
          <Search aria-hidden /> Search or jump to… <kbd className="ml-auto rounded border px-1 font-mono text-xs">⌘K</kbd>
        </Button>
      ) : (
        <Button variant="ghost" size="icon" aria-label="Search or jump to…" onClick={onOpenPalette}>
          <Search aria-hidden />
        </Button>
      )}
      <div className="flex-1" />
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" aria-label="Runtime status" className="rounded-full">
            <RuntimePill state={r.state} version={r.version} iconOnly={!isLarge} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Version</dt>
            <dd>{r.version ?? "—"}</dd>
            <dt className="text-muted-foreground">Instance</dt>
            <dd className="truncate font-mono text-xs">{r.instanceId ?? "—"}</dd>
            <dt className="text-muted-foreground">Platform</dt>
            <dd>{r.platform ?? "—"}</dd>
            <dt className="text-muted-foreground">OC checkout</dt>
            <dd className="truncate font-mono text-xs">{r.ocPath ? `${r.ocPath} @ ${r.ocCommit ?? "?"}` : "—"}</dd>
            <dt className="text-muted-foreground">Last check</dt>
            <dd>{r.lastCheckAt ? formatRelative(r.lastCheckAt) : "—"}</dd>
          </dl>
          <div className="mt-3 flex gap-2">
            {(shell.user?.role === "TESTER" || shell.user?.role === "ADMIN") && (
              <Button size="sm" variant="outline" onClick={shell.runEnvironmentCheck}>
                Run environment check
              </Button>
            )}
            {blocked ? (
              <Button size="sm" variant="ghost" onClick={() => setAllowHelp(true)}>
                <ShieldAlert aria-hidden /> How to allow access
              </Button>
            ) : (
              <Button size="sm" variant="ghost" asChild>
                <Link to="/help#start">How to start ATS</Link>
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <PermissionBlockedDialog open={allowHelp && blocked} onOpenChange={setAllowHelp} onRetry={shell.retryRuntime} />
      {shell.activeRun && (
        <Button variant="outline" size="sm" asChild className="hidden md:inline-flex">
          <Link to={`/runs/${shell.activeRun.runId}`} aria-label={`Run in progress · ${shell.activeRun.done}/${shell.activeRun.total}`}>
            <LoaderCircle aria-hidden className="animate-spin" />
            Run in progress · {shell.activeRun.done}/{shell.activeRun.total}
          </Link>
        </Button>
      )}
      <AdminBell />
      {shell.user?.role === "ADMIN" && (
        <Button variant="outline" size="sm" asChild className="hidden md:inline-flex">
          <Link to={inAdmin ? "/" : "/admin"}>
            <Shield aria-hidden />
            {inAdmin ? "Tester dashboard" : "Go to admin dashboard"}
          </Link>
        </Button>
      )}
      <HelpMenu />
      <ThemeMenu />
      <UserMenu />
    </header>
  );
}
